import Vue from "vue";
import { createActivityReporter } from "@/util/presence/activityReporter";
import { PRESENCE_CONFIG_FIELD } from "@/util/presence/presenceView";

declare module "vue/types/vue" {
    interface Vue {
        /** Tells the DataHub this user just used the app. Installed in dashboardSetup. */
        $reportActivity?: () => void;
    }
}

const ACTIVITY_EVENTS = ["mousemove", "keydown", "pointerdown"] as const;

/**
 * Reports user activity to the server, at most once a minute. Shared by presence tracking and the
 * inactivity timeout (the server relays each report to the user's other copies of the app), so a tab
 * with both on still sends one report a minute rather than two.
 */
export const reportUserActivity = createActivityReporter(() => Vue.prototype.$reportActivity?.());

let initialized = false;

/**
 * Reports mouse and keyboard input to the server so the Users page can tell an active user from an
 * idle one. Deliberately independent of the inactivity timeout: tenants that leave auto sign-out off
 * still get the Idle status. Does nothing when the tenant has presence tracking switched off.
 */
export async function presenceSetup(app: Vue): Promise<void> {
    if (initialized) return;
    initialized = true;

    try {
        const resp = await app.$http.get(`/configuration/${PRESENCE_CONFIG_FIELD}`);
        if (resp.data === false) return;
    } catch {
        // Unknown is treated as on: the server ignores reports while tracking is off anyway.
    }

    for (const event of ACTIVITY_EVENTS) {
        window.addEventListener(event, reportUserActivity, { passive: true });
    }
}
